import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock, Loader2, ArrowRight, Shield, Mail, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import "@/components/FooterHero.css";

const PHOTOS = [
  "https://static.prod-images.emergentagent.com/jobs/a4e7a419-71b0-4f59-8501-640e90382c41/images/dd8635bc0064e4c3e766456c157833579f292653a2c73bf499b4576d7441e265.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/a4e7a419-71b0-4f59-8501-640e90382c41/images/8c481a12161d5d6e8c66eaf357d0199d60af674d27695e36a50eeecba9b264c9.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/a4e7a419-71b0-4f59-8501-640e90382c41/images/9e8ad3797c2c7425be558e629368aa6547f53633483cd8de2367efecda23d8c4.jpeg",
];

function shuffle(a) {
  const x = [...a];
  for (let i = x.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [x[i], x[j]] = [x[j], x[i]];
  }
  return x;
}

export function AuthScene({ children }) {
  const slides = useMemo(() => shuffle(PHOTOS), []);
  const SLIDE = 7;
  const total = slides.length * SLIDE;
  return (
    <section className="fh-section" style={{ minHeight: "100vh" }} data-testid="auth-scene">
      <div className="fh-image-wrap">
        {slides.map((url, i) => (
          <div
            key={`${url}-${i}`}
            className="fh-image"
            style={{
              backgroundImage: `url('${url}')`,
              animationName: "fh-slide",
              animationTimingFunction: "ease-in-out",
              animationIterationCount: "infinite",
              animationFillMode: "both",
              animationDuration: `${total}s`,
              animationDelay: `${i * SLIDE}s`,
            }}
          />
        ))}
      </div>
      <div className="fh-overlay" style={{ opacity: 0.78 }} />
      <div className="fh-corner-pattern" aria-hidden="true" />
      <HexBackdrop />
      <div className="fh-content" style={{ flex: 1, minHeight: "100vh" }}>
        <div className="fh-inner" style={{ maxWidth: 520 }}>
          {children}
        </div>
      </div>
    </section>
  );
}

export function AuthCard({ children }) {
  return (
    <div
      className="rounded-2xl p-5 sm:p-7"
      style={{
        background: "rgba(17, 24, 39, 0.55)",
        border: "1px solid rgba(255,255,255,0.12)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        boxShadow: "0 20px 60px -20px rgba(0,0,0,0.7)",
      }}
    >
      {children}
    </div>
  );
}

export function AuthInput({ label, type = "text", value, onChange, placeholder, icon, testId, autoFocus, autoComplete }) {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  return (
    <label className="block mb-4">
      <span className="block text-[11px] uppercase tracking-[0.18em] text-sky-300 font-bold mb-2">
        {label}
      </span>
      <div className="relative">
        {icon ? <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{icon}</span> : null}
        <input
          type={isPassword && show ? "text" : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          className={`w-full rounded-lg bg-white/95 text-gray-900 ${icon ? "pl-9" : "px-3"} ${isPassword ? "pr-10" : "pr-3"} py-3 text-base outline-none focus:ring-2 focus:ring-sky-300`}
          placeholder={placeholder}
          data-testid={testId}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            aria-label={show ? "Hide password" : "Show password"}
            data-testid={testId ? `${testId}-toggle` : undefined}
            tabIndex={-1}
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
    </label>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      const to = location.state?.from?.pathname || (user.must_change_password ? "/welcome" : "/");
      navigate(to, { replace: true });
    }
  }, [user, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      const to = u.must_change_password ? "/welcome" : (location.state?.from?.pathname || "/");
      navigate(to, { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Sign in failed. Check your email and password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthScene>
      <div className="fh-eyebrow" style={{ color: "#2899FF" }}>
        HTM Project Hub
      </div>
      <h1 className="fh-heading" style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}>
        Sign in
      </h1>
      <p className="fh-text" style={{ maxWidth: "44ch" }}>
        Submit projects, manage your case studies and view the team's work.
      </p>
      <AuthCard>
        <form onSubmit={submit}>
          <AuthInput
            label="Email"
            type="text"
            value={email}
            onChange={setEmail}
            placeholder="you@company.com"
            icon={<Mail className="h-4 w-4" />}
            testId="login-email"
            autoFocus
            autoComplete="email"
          />
          <AuthInput
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            icon={<Lock className="h-4 w-4" />}
            testId="login-password"
            autoComplete="current-password"
          />
          {err ? (
            <div className="text-sm text-red-300 mb-3" data-testid="login-error">{err}</div>
          ) : null}
          <button
            type="submit"
            disabled={busy}
            className="fh-btn-primary w-full"
            data-testid="login-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="mt-4 flex items-center justify-between text-xs text-white/70">
            <Link to="/forgot-password" className="hover:text-sky-300 underline-offset-4 hover:underline" data-testid="login-forgot">
              Forgot password?
            </Link>
            <span className="inline-flex items-center gap-2 text-white/55">
              <Shield className="h-3.5 w-3.5" /> Staff only
            </span>
          </div>
        </form>
      </AuthCard>
    </AuthScene>
  );
}

function HexBackdrop() {
  return (
    <svg
      className="fh-svg"
      viewBox="0 0 1200 700"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      style={{ opacity: 0.85 }}
    >
      <g className="fh-hex-base">
        {hexCluster().map((p, i) => <path key={i} d={p} />)}
      </g>
      <g className="fh-hex-chase">
        {hexCluster().map((p, i) => {
          const dur = 8 + (i % 6) * 2;
          const delay = (i * 0.4) % 3;
          const dir = i % 3 === 0 ? "reverse" : "normal";
          const accent = i % 5 === 0;
          return (
            <path
              key={`c${i}`}
              d={p}
              pathLength="100"
              className="fh-chase"
              style={{
                "--dur": `${dur}s`,
                "--delay": `${delay}s`,
                animationDirection: dir,
                stroke: accent ? "#2899FF" : "rgba(255,255,255,0.7)",
              }}
            />
          );
        })}
      </g>
    </svg>
  );
}

function hexCluster() {
  // Tessellating road-sign triangles (equilateral, alternating up/down)
  const S = 96;
  const H = (S * Math.sqrt(3)) / 2;
  const paths = [];
  const strip = (x0, y, count) => {
    for (let t = 0; t < count; t++) {
      const x = x0 + (t * S) / 2;
      const up = t % 2 === 0;
      const pts = up
        ? [[x, y], [x + S, y], [x + S / 2, y - H]]
        : [[x, y - H], [x + S, y - H], [x + S / 2, y]];
      paths.push(
        `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)} L ${pts[1][0].toFixed(1)} ${pts[1][1].toFixed(1)} L ${pts[2][0].toFixed(1)} ${pts[2][1].toFixed(1)} Z`
      );
    }
  };
  // Bottom-right cluster (rows shrink going up)
  strip(1180 - 5 * S, 690, 9);
  strip(1180 - 4 * S, 690 - H, 7);
  strip(1180 - 3 * S, 690 - 2 * H, 5);
  return paths;
}
